<template>
    <div>
        <div class="flex text-lg mb-4">
            <h1 class="pr-4 border-r uppercase font-bold">NAWEC Recovery Report</h1>
            <h1 class="px-4 border-r">
                From:
                <span class="font-bold">{{
                    $route.params.from | moment("ddd, MMM Do YYYY")
                }}</span>
            </h1>
            <h1 class="px-4">
                To:
                <span class="font-bold">{{
                    $route.params.to | moment("ddd, MMM Do YYYY")
                }}</span>
            </h1>
        </div>

        <!-- Key Metrics Summary -->
        <div class="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6" v-if="statistics">
            <!-- Total Recovered Count -->
            <div class="bg-white border border-gray-200 rounded-lg p-4 shadow-sm">
                <div class="flex items-center">
                    <div class="flex-shrink-0 p-3 rounded-full bg-green-100">
                        <svg class="w-6 h-6 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                                d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"></path>
                        </svg>
                    </div>
                    <div class="ml-4">
                        <div class="text-xs font-medium text-gray-500 uppercase mb-1">Total Recovered</div>
                        <div class="text-2xl font-bold text-gray-900">{{ statistics.total_recovered }}</div>
                        <div class="text-xs text-gray-400">transactions</div>
                    </div>
                </div>
            </div>

            <!-- Total Amount Recovered -->
            <div class="bg-white border border-gray-200 rounded-lg p-4 shadow-sm">
                <div class="flex items-center">
                    <div class="flex-shrink-0 p-3 rounded-full bg-blue-100">
                        <svg class="w-6 h-6 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                                d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z">
                            </path>
                        </svg>
                    </div>
                    <div class="ml-4">
                        <div class="text-xs font-medium text-gray-500 uppercase mb-1">Total Amount</div>
                        <div class="text-2xl font-bold text-gray-900">GMD {{ formatPrice(statistics.total_original_amount) }}</div>
                        <div class="text-xs text-gray-400">recovered value</div>
                    </div>
                </div>
            </div>

            <!-- Average Daily Recovery -->
            <div class="bg-white border border-gray-200 rounded-lg p-4 shadow-sm">
                <div class="flex items-center">
                    <div class="flex-shrink-0 p-3 rounded-full bg-purple-100">
                        <svg class="w-6 h-6 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                                d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z">
                            </path>
                        </svg>
                    </div>
                    <div class="ml-4">
                        <div class="text-xs font-medium text-gray-500 uppercase mb-1">Avg Daily Recovery</div>
                        <div class="text-2xl font-bold text-gray-900">GMD {{ formatPrice(avgDailyAmount) }}</div>
                        <div class="text-xs text-gray-400">{{ avgDailyCount }} txns/day</div>
                    </div>
                </div>
            </div>
        </div>

        <!-- Records Table -->
        <div v-if="records && records.length > 0">
            <div class="flex items-center justify-between mb-3">
                <h2 class="text-lg font-semibold text-gray-900">Recovery Records</h2>
                <span class="text-sm text-gray-500">{{ records.length }} record(s)</span>
            </div>
            <div class="bg-white border border-gray-200 rounded-lg overflow-hidden overflow-x-auto">
                <table class="min-w-full divide-y divide-gray-200">
                    <thead class="bg-gray-50">
                        <tr>
                            <th scope="col" class="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                #
                            </th>
                            <th scope="col" class="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                Meter / Customer
                            </th>
                            <th scope="col" class="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                                Sale Amount
                            </th>
                            <th scope="col" class="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                Original Request
                            </th>
                            <th scope="col" class="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                Recovered At
                            </th>
                            <th scope="col" class="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                Recovered By
                            </th>
                        </tr>
                    </thead>
                    <tbody class="bg-white divide-y divide-gray-200">
                        <tr v-for="(record, index) in records" :key="'record-' + record.id" class="hover:bg-gray-50">
                            <td class="px-4 py-4 whitespace-nowrap text-sm text-gray-500">
                                {{ index + 1 }}
                            </td>
                            <td class="px-4 py-4 whitespace-nowrap">
                                <div class="text-sm font-medium text-gray-900">{{ record.meter_serial_no }}</div>
                                <div class="text-xs text-gray-500">{{ record.customer_name || 'N/A' }}</div>
                            </td>
                            <td class="px-4 py-4 whitespace-nowrap text-right">
                                <span class="text-sm font-semibold text-gray-900">GMD {{ formatPrice(record.sale_amount) }}</span>
                            </td>
                            <td class="px-4 py-4 whitespace-nowrap">
                                <div class="text-sm text-gray-900">{{ record.request_time | moment("MMM DD, YYYY") }}</div>
                                <div class="text-xs text-gray-500">{{ record.request_time | moment("HH:mm") }}</div>
                            </td>
                            <td class="px-4 py-4 whitespace-nowrap">
                                <div class="text-sm text-gray-900">{{ record.recovered_at | moment("MMM DD, YYYY") }}</div>
                                <div class="text-xs text-gray-500">{{ record.recovered_at | moment("HH:mm") }}</div>
                            </td>
                            <td class="px-4 py-4 whitespace-nowrap">
                                <span class="inline-flex items-center px-2 py-1 rounded-md text-xs font-medium bg-gray-100 text-gray-800">
                                    {{ record.recovered_by }}
                                </span>
                            </td>
                        </tr>
                    </tbody>
                </table>
            </div>
        </div>

        <!-- Empty State -->
        <div v-if="!records || records.length === 0" class="text-center py-12">
            <svg class="mx-auto h-12 w-12 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                    d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z">
                </path>
            </svg>
            <h3 class="mt-2 text-sm font-medium text-gray-900">No recovery records found</h3>
            <p class="mt-1 text-sm text-gray-500">No NAWEC recovery transactions were found for the selected date range.</p>
        </div>
    </div>
</template>

<script>
export default {
    name: 'NawecRecoveryReport',
    
    props: {
        data: {
            type: Object,
            required: true,
        },
    },

    computed: {
        // Handle both nested (API response) and direct data structures
        reportData() {
            return this.data?.data || this.data || {};
        },
        records() {
            return this.reportData?.records || [];
        },
        statistics() {
            return this.reportData?.statistics || null;
        },
        dailyBreakdown() {
            return this.reportData?.daily_breakdown || [];
        },
        avgDailyAmount() {
            if (!this.dailyBreakdown || this.dailyBreakdown.length === 0) return 0;
            const totalAmount = this.dailyBreakdown.reduce((sum, day) => sum + Number(day.total_amount), 0);
            return totalAmount / this.dailyBreakdown.length;
        },
        avgDailyCount() {
            if (!this.dailyBreakdown || this.dailyBreakdown.length === 0) return 0;
            const totalCount = this.dailyBreakdown.reduce((sum, day) => sum + day.count, 0);
            return (totalCount / this.dailyBreakdown.length).toFixed(1);
        },
    },

    methods: {
        formatPrice(value) {
            if (value === null || value === undefined) return '0.00';
            return Number(value).toLocaleString('en-US', {
                minimumFractionDigits: 2,
                maximumFractionDigits: 2,
            });
        },
    },
};
</script>
